import type { CSSProperties } from "react";

/** Portato da `components/core/Avatar.jsx` del design system. */

const PALETTE = ["#1A1A1A", "#6E6E6E", "#9B92C9", "#C98A72", "#7E9A87"] as const;

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();
}

/** Colore stabile derivato dal nome: la stessa persona ha sempre la stessa tinta. */
function colorFor(name: string): string {
  const seed = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PALETTE[seed % PALETTE.length] ?? PALETTE[0];
}

export interface AvatarProps {
  name?: string;
  src?: string;
  size?: number;
  ring?: boolean;
  style?: CSSProperties;
}

export function Avatar({ name = "", src, size = 32, ring = false, style }: AvatarProps) {
  return (
    <div
      title={name}
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "var(--radius-pill)",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: src ? "var(--surface-sunken)" : colorFor(name),
        color: "var(--white)",
        fontFamily: "var(--font-display)",
        fontWeight: 600,
        fontSize: Math.round(size * 0.32),
        letterSpacing: "-0.02em",
        boxShadow: ring ? "0 0 0 2px var(--white)" : "none",
        ...style,
      }}
    >
      {src ? (
        <img src={src} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials(name)
      )}
    </div>
  );
}

export interface AvatarStackProps {
  people: { name: string; src?: string }[];
  /**
   * Quante persone ci sono **davvero**, se più di quelle passate.
   *
   * Serve quando chi chiama ne riceve solo le prime: senza, un gruppo da venti
   * di cui arrivano quattro nomi mostrerebbe «+1» invece di «+16».
   */
  total?: number;
  size?: number;
  max?: number;
  style?: CSSProperties;
}

export function AvatarStack({ people, total, size = 26, max = 3, style }: AvatarStackProps) {
  const shown = people.slice(0, max);
  const rest = Math.max(total ?? people.length, people.length) - shown.length;

  return (
    <div style={{ display: "inline-flex", alignItems: "center", ...style }}>
      {shown.map((person, index) => (
        <div key={index} style={{ marginLeft: index === 0 ? 0 : -size * 0.28 }}>
          <Avatar name={person.name} src={person.src} size={size} ring />
        </div>
      ))}

      {rest > 0 && (
        <div
          style={{
            marginLeft: -size * 0.28,
            width: size,
            height: size,
            borderRadius: "var(--radius-pill)",
            background: "var(--ink-100)",
            color: "var(--ink-600)",
            boxShadow: "0 0 0 2px var(--white)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-display)",
            fontSize: Math.round(size * 0.3),
            fontWeight: 600,
          }}
        >
          +{rest}
        </div>
      )}
    </div>
  );
}
